import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  LayoutDashboard,
  MapPin,
  Globe,
  Building,
  Users,
  AlertCircle,
  ClipboardList,
  Radio,
  Link2,
  Settings,
  ChevronDown,
  Bell,
  BellRing,
  LogOut,
  Library,
  FileBarChart,
  Boxes,
  User,
  Cpu,
  Plug,
  Network,
  Store,
  KeyRound,
  Search,
  Languages,
  X,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  openIncidentModal,
  logoutModalToggle,
  setSidebarCollapsed,
} from "@/Redux/Action/organizationActions";
import RecentIncidents from "@/components/RecentIncidents";
import { getWebAccessLevel, hasActiveSupportInstitutionSession, isWebAdmin, WEB_ACCESS } from "@/auth/webAccess";
import Logo from "../assets/Img/logo.png"
import SaaSLogo from "../assets/brand-assets/buoy-mark.svg";
import LogoutModal from "@/Modal/LogoutModal";
import {
  canManageInstitutionAccess,
  isPlatformSuperadmin,
} from "@/auth/saasSession";
import { isFeatureEnabled, LICENSE_FEATURES } from "@/auth/featureEntitlements";
import { creationAccessForUser } from "../auth/incidentPermissions";
import { SaaSInstitutionsNavigation } from "@/components/InstitutionSwitcher";
import { SAAS_INSTITUTION_SELECTION_EVENT } from "../auth/saasControlPlaneSelection";
import {
  globalMarketplaceHref,
  isGlobalMarketplaceSearch,
  SAAS_ADMINISTRATION_SECTION_EVENT,
} from "@/page/saasAdministrationNavigation";
import { useNotifications } from "@/hooks/useNotifications";
import {
  institutionSidebarPreferenceKey,
  readInstitutionSidebarPreference,
  writeInstitutionSidebarPreference,
} from "./institutionSidebarPreference";
import "./SaaSShell.css";

const SAAS_LANGUAGE_KEY = 'disasterms_saas_language';
const SAAS_LANGUAGE_EVENT = 'disasterms:saas-language-changed';

const readSaaSLanguage = () => {
  if (typeof window === 'undefined') return 'en';
  return window.localStorage.getItem(SAAS_LANGUAGE_KEY) === 'ar' ? 'ar' : 'en';
};

const SaaSNavItem = ({ icon: Icon, text, to, active, collapsed, badge, dot, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    title={collapsed ? text : undefined}
    aria-label={collapsed ? text : undefined}
    className={`group relative flex h-11 items-center rounded-[9px] border transition-all duration-200
      ${active
        ? 'border-[#28ade3]/40 bg-[#28ade3]/10 text-[#28ade3]'
        : `${collapsed ? 'border-[#96a5d2]/20' : 'border-transparent'} text-[#5f6890] hover:border-white/20 hover:bg-white/[0.035] hover:text-[#d7dcef]`}
      ${collapsed ? 'mx-auto w-11 justify-center p-0' : 'w-full gap-3 px-3'}`}
  >
    <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.8} />
    {!collapsed && <span className="min-w-0 flex-1 truncate text-[13px] font-medium">{text}</span>}
    {!collapsed && badge && (
      <span
        className="inline-flex min-w-[20px] items-center justify-center rounded-full bg-[#ef3937] px-1.5 py-0.5 text-[10px] font-bold leading-4 text-white"
        aria-label={`${badge} unread`}
      >
        {badge}
      </span>
    )}
    {collapsed && dot && (
      <span className="absolute right-[8px] top-[7px] h-2 w-2 rounded-full bg-[#ef3937] ring-2 ring-[#0a0e24]" aria-label={`${badge || ''} unread`} />
    )}
  </Link>
);

export const SaaSNotificationsNavItem = ({ text, active, collapsed }) => {
  const { unreadCount } = useNotifications();
  const displayCount = unreadCount > 99 ? '99+' : unreadCount;

  return (
    <SaaSNavItem
      icon={BellRing}
      text={text}
      to="/notifications"
      active={active}
      collapsed={collapsed}
      badge={unreadCount > 0 ? displayCount : null}
      dot={unreadCount > 0}
    />
  );
};

/* -------------------------------------------------------------------------- */
/*                                NAV ITEM                                   */
/* -------------------------------------------------------------------------- */
/**
 * @param {{ icon: React.ElementType, text: string, to: string, active: boolean, collapsed: boolean }}
 */
const NavItem = ({
  icon: Icon,
  text,
  to,
  active,
  collapsed,
  badge,
  dot,
  onClick,
}) => (
  <Link
    to={to}
    onClick={onClick}
    title={collapsed ? text : undefined}
    aria-label={collapsed ? text : undefined}
    aria-current={active ? "page" : undefined}
    className={`group relative flex h-11 items-center rounded-[9px] border transition-colors duration-150
      ${active
        ? "border-blue-200 bg-blue-50 text-blue-700"
        : `${collapsed ? "border-slate-200" : "border-transparent"} text-slate-600 hover:border-slate-200 hover:bg-slate-50 hover:text-slate-950`
      }
      ${collapsed ? "mx-auto w-11 justify-center p-0" : "w-full gap-3 px-3"}`}
  >
    <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.8} />
    {!collapsed && <span className="min-w-0 flex-1 truncate text-[13px] font-medium">{text}</span>}
    {!collapsed && badge && (
      <span
        className="inline-flex min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold leading-4 text-white"
        aria-label={`${badge} unread`}
      >
        {badge}
      </span>
    )}
    {collapsed && dot && (
      <span
        className="absolute right-[7px] top-[6px] h-2 w-2 rounded-full bg-red-500 ring-2 ring-white"
        aria-label={`${badge || ""} unread`}
      />
    )}
  </Link>
);

export const InstitutionNotificationsNavItem = ({ text, active, collapsed }) => {
  const { unreadCount } = useNotifications();
  const displayCount = unreadCount > 99 ? "99+" : unreadCount;

  return (
    <NavItem
      icon={BellRing}
      text={text}
      to="/notifications"
      active={active}
      collapsed={collapsed}
      badge={unreadCount > 0 ? displayCount : null}
      dot={unreadCount > 0}
    />
  );
};

/* -------------------------------------------------------------------------- */
/*                             SIDEBAR COMPONENT                             */
/* -------------------------------------------------------------------------- */
const Sidebar = ({ mobile = false, onRequestClose }) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const { isSidebarCollapsed: isCollapsed, isLogoutModalOpen, dashboardName } = useSelector((state) => state.organization);
  let currentUser = null;
  try {
    currentUser = JSON.parse(localStorage.getItem('user') || 'null');
  } catch (_) {
    currentUser = null;
  }
  const isAdmin = isWebAdmin(currentUser);
  const webAccessLevel = getWebAccessLevel(currentUser);
  const activeSupportSession = hasActiveSupportInstitutionSession(currentUser);
  const canManageAccess = canManageInstitutionAccess(currentUser);
  const isSaaSSuperadmin = isPlatformSuperadmin(currentUser);
  const reportsEnabled = isFeatureEnabled(currentUser, LICENSE_FEATURES.REPORTS);
  const iotEnabled = isFeatureEnabled(currentUser, LICENSE_FEATURES.IOT);
  const apiConnectionsEnabled = isFeatureEnabled(currentUser, LICENSE_FEATURES.API_CONNECTIONS);
  const { canCreateIncident, canCreateDrill } = creationAccessForUser(currentUser);
  const canCreateIncidentOrDrill = canCreateIncident || canCreateDrill;
  const globalMarketplaceOpen = isGlobalMarketplaceSearch(location.search);
  const institutionPreferenceKey = institutionSidebarPreferenceKey(currentUser);

  const [setupOpen, setSetupOpen] = useState(false);
  const [saasSearch, setSaaSSearch] = useState('');
  const [saasLanguage, setSaaSLanguage] = useState(readSaaSLanguage);
  const [selectedSaaSInstitutionId, setSelectedSaaSInstitutionId] = useState(() => (
    new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search).get('institution') || ''
  ));
  const saasSearchInputRef = useRef(null);
  const hydratedInstitutionPreferenceRef = useRef("");

  useLayoutEffect(() => {
    if (isSaaSSuperadmin) return;
    if (hydratedInstitutionPreferenceRef.current === institutionPreferenceKey) return;

    hydratedInstitutionPreferenceRef.current = institutionPreferenceKey;
    const savedPreference = readInstitutionSidebarPreference(currentUser);
    if (savedPreference !== null && savedPreference !== isCollapsed) {
      dispatch(setSidebarCollapsed(savedPreference));
    }
  }, [
    currentUser,
    dispatch,
    institutionPreferenceKey,
    isCollapsed,
    isSaaSSuperadmin,
  ]);

  useEffect(() => {
    if (!isSaaSSuperadmin) return undefined;
    const handleInstitutionSelection = (event) => {
      setSelectedSaaSInstitutionId(String(event.detail?.institutionId || ''));
    };
    const handleSectionSelection = (event) => {
      if (event.detail?.section === 'institutions' || event.detail?.section === 'marketplace') {
        setSelectedSaaSInstitutionId('');
      }
    };
    window.addEventListener(SAAS_INSTITUTION_SELECTION_EVENT, handleInstitutionSelection);
    window.addEventListener(SAAS_ADMINISTRATION_SECTION_EVENT, handleSectionSelection);
    return () => {
      window.removeEventListener(SAAS_INSTITUTION_SELECTION_EVENT, handleInstitutionSelection);
      window.removeEventListener(SAAS_ADMINISTRATION_SECTION_EVENT, handleSectionSelection);
    };
  }, [isSaaSSuperadmin]);

  useEffect(() => {
    const queryInstitutionId = new URLSearchParams(location.search || '').get('institution') || '';
    if (queryInstitutionId) setSelectedSaaSInstitutionId(queryInstitutionId);
    else setSelectedSaaSInstitutionId('');
  }, [location.pathname, location.search]);

  /* ------------------------------- Handlers ------------------------------ */
  const toggleSidebar = () => {
    const nextCollapsed = !isCollapsed;
    if (!isSaaSSuperadmin) {
      writeInstitutionSidebarPreference(currentUser, nextCollapsed);
    }
    dispatch(setSidebarCollapsed(nextCollapsed));
  };

  const handleNewIncident = () => {
    onRequestClose?.();
    dispatch(openIncidentModal());
  };

  const handleLogout = () => dispatch(logoutModalToggle(true));

  /* ------------------------------- Setup Links --------------------------- */
  const setupLinks = [
    { icon: MapPin, text: "Locations & Zones", to: "/locations-zones" },
    { icon: Building, text: "Departments & Units", to: "/departments" },
    { icon: User, text: "Users & Access", to: "/users" },
    { icon: AlertCircle, text: "Incident Classification", to: "/incidents" },
    { icon: ClipboardList, text: "Action Cards", to: "/actionscards" },
    { icon: Users, text: "Teams & Crews", to: "/teams" },
    { icon: Boxes, text: "Resources", to: "/resources" },
    ...(iotEnabled ? [{ icon: Cpu, text: "IoT Devices", to: "/iot-sensors" }] : []),
    ...(apiConnectionsEnabled ? [{ icon: Plug, text: "API Connections", to: "/api-connections" }] : []),
  ];

  if (isSaaSSuperadmin) {
    const isArabic = saasLanguage === 'ar';
    const copy = isArabic ? {
      administration: 'إدارة المنصة',
      notifications: 'الإشعارات',
      marketplace: 'المتجر',
      search: 'بحث في المؤسسات',
      searchAction: 'بحث',
      logout: 'تسجيل الخروج',
      language: 'اللغة',
      expand: 'توسيع الشريط الجانبي',
      collapse: 'طي الشريط الجانبي',
      navigation: 'التنقل في منصة SaaS',
    } : {
      administration: 'SaaS Administration',
      notifications: 'Notifications',
      marketplace: 'Marketplace',
      search: 'Search institutions',
      searchAction: 'Search',
      logout: 'Logout',
      language: 'Language',
      expand: 'Expand sidebar',
      collapse: 'Collapse sidebar',
      navigation: 'SaaS platform navigation',
    };
    const overviewActive = location.pathname.startsWith('/saas-administration')
      && !globalMarketplaceOpen
      && !selectedSaaSInstitutionId;
    const notificationsActive = location.pathname === '/notifications';
    const marketplaceActive = location.pathname.startsWith('/saas-administration') && globalMarketplaceOpen;

    const chooseLanguage = (language) => {
      setSaaSLanguage(language);
      window.localStorage.setItem(SAAS_LANGUAGE_KEY, language);
      window.dispatchEvent(new window.CustomEvent(SAAS_LANGUAGE_EVENT, { detail: { language } }));
    };

    const openCollapsedSearch = () => {
      if (isCollapsed) dispatch(setSidebarCollapsed(false));
      window.setTimeout(() => saasSearchInputRef.current?.focus(), 200);
    };

    return (
      <aside
        className={`dms-saas-shell flex shrink-0 flex-col overflow-hidden bg-[#0a0e24] text-white transition-[width] duration-300 ease-out ${isArabic ? 'shadow-[-10px_0_35px_rgba(0,0,0,0.18)]' : 'shadow-[10px_0_35px_rgba(0,0,0,0.18)]'} ${isCollapsed ? 'w-[72px]' : 'w-[264px]'}`}
        dir={isArabic ? 'rtl' : 'ltr'}
        data-testid="saas-platform-sidebar"
      >
        <header className={`flex h-[92px] shrink-0 items-center border-b border-white/[0.08] ${isCollapsed ? 'justify-center px-2' : 'px-5'}`}>
          <button
            type="button"
            onClick={toggleSidebar}
            className={`group flex min-w-0 items-center rounded-xl text-left outline-none transition-colors hover:bg-white/[0.035] focus-visible:ring-2 focus-visible:ring-[#28ade3]/60 ${isCollapsed ? 'h-12 w-12 justify-center' : 'w-full gap-3 px-1 py-2'}`}
            aria-label={isCollapsed ? copy.expand : copy.collapse}
            title={isCollapsed ? copy.expand : copy.collapse}
          >
            <img src={SaaSLogo} alt="DisasterMS" className="h-11 w-11 shrink-0 object-contain" />
            {!isCollapsed && (
              <span className="min-w-0 leading-none">
                <span className="block truncate text-[18px] font-bold tracking-[-0.02em] text-white">DisasterMS</span>
                <span className="mt-2 block whitespace-nowrap text-[8px] font-bold tracking-[0.2em] text-[#28ade3]">PLAN · ACT · LEAD</span>
              </span>
            )}
          </button>
        </header>

        <nav className="dms-saas-shell-scroll flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden px-3 py-4" aria-label={copy.navigation}>
          {!isCollapsed && (
            <label className="relative mb-4 block">
              <span className="sr-only">{copy.search}</span>
              <Search className={`pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-[#5f6890] ${isArabic ? 'right-3' : 'left-3'}`} />
              <input
                ref={saasSearchInputRef}
                type="search"
                value={saasSearch}
                onChange={(event) => setSaaSSearch(event.target.value)}
                placeholder={copy.search}
                aria-label={copy.search}
                className={`h-[42px] w-full rounded-[9px] border border-[#96a5d2]/20 bg-[#0a0e24] text-[12px] text-[#d7dcef] outline-none transition placeholder:text-[#5f6890] focus:border-[#28ade3] focus:ring-[3px] focus:ring-[#28ade3]/15 ${isArabic ? 'pr-9 pl-3' : 'pl-9 pr-3'}`}
              />
            </label>
          )}

          <div className="space-y-1.5">
            <SaaSNavItem
              icon={Network}
              text={copy.administration}
              to="/saas-administration"
              active={overviewActive}
              collapsed={isCollapsed}
              onClick={() => window.dispatchEvent(new window.CustomEvent(SAAS_ADMINISTRATION_SECTION_EVENT, { detail: { section: 'institutions' } }))}
            />
            <SaaSNotificationsNavItem
              text={copy.notifications}
              active={notificationsActive}
              collapsed={isCollapsed}
            />
            {isCollapsed && (
              <button
                type="button"
                onClick={openCollapsedSearch}
                className="mx-auto flex h-11 w-11 items-center justify-center rounded-[9px] border border-[#96a5d2]/20 text-[#5f6890] transition-colors hover:border-white/20 hover:bg-white/[0.035] hover:text-[#d7dcef]"
                title={copy.searchAction}
                aria-label={copy.searchAction}
              >
                <Search className="h-[18px] w-[18px]" strokeWidth={1.8} />
              </button>
            )}
            <SaaSNavItem
              icon={Store}
              text={copy.marketplace}
              to={globalMarketplaceHref()}
              active={marketplaceActive}
              collapsed={isCollapsed}
              onClick={() => window.dispatchEvent(new window.CustomEvent(SAAS_ADMINISTRATION_SECTION_EVENT, { detail: { section: 'marketplace' } }))}
            />
          </div>

          <SaaSInstitutionsNavigation
            collapsed={isCollapsed}
            searchValue={saasSearch}
            onSearchChange={setSaaSSearch}
            showSearch={false}
            language={saasLanguage}
          />
        </nav>

        <footer className={`shrink-0 border-t border-white/[0.08] ${isCollapsed ? 'space-y-2 px-3 py-3' : 'space-y-3 px-4 py-4'}`}>
          {isCollapsed ? (
            <button
              type="button"
              onClick={() => chooseLanguage(isArabic ? 'en' : 'ar')}
              className="mx-auto flex h-11 w-11 items-center justify-center rounded-[9px] border border-[#96a5d2]/20 text-[#8a92b0] transition-colors hover:border-white/20 hover:bg-white/[0.035] hover:text-white"
              title={copy.language}
              aria-label={copy.language}
            >
              <Languages className="h-[18px] w-[18px]" strokeWidth={1.8} />
            </button>
          ) : (
            <div className="grid h-10 grid-cols-2 rounded-[10px] border border-[#96a5d2]/20 bg-[#0a0e24] p-1" role="group" aria-label={copy.language}>
              <button
                type="button"
                onClick={() => chooseLanguage('ar')}
                aria-pressed={isArabic}
                className={`rounded-[7px] text-[11px] font-medium transition-colors ${isArabic ? 'bg-[#163e8e] text-white shadow-sm' : 'text-[#8a92b0] hover:text-[#d7dcef]'}`}
              >
                عربي
              </button>
              <button
                type="button"
                onClick={() => chooseLanguage('en')}
                aria-pressed={!isArabic}
                className={`rounded-[7px] text-[11px] font-medium transition-colors ${!isArabic ? 'bg-[#163e8e] text-white shadow-sm' : 'text-[#8a92b0] hover:text-[#d7dcef]'}`}
              >
                English
              </button>
            </div>
          )}

          <button
            type="button"
            onClick={handleLogout}
            title={isCollapsed ? copy.logout : undefined}
            aria-label={copy.logout}
            className={`flex h-11 w-full items-center rounded-[9px] border border-transparent text-[#8a92b0] transition-colors hover:border-[#ef3937]/25 hover:bg-[#ef3937]/10 hover:text-[#ef3937] ${isCollapsed ? 'justify-center p-0' : 'gap-3 px-3'}`}
          >
            <LogOut className="h-[18px] w-[18px] shrink-0" strokeWidth={1.8} />
            {!isCollapsed && <span className="text-[13px] font-medium">{copy.logout}</span>}
          </button>
        </footer>
        {isLogoutModalOpen && <LogoutModal isSaaS onClose={() => dispatch(logoutModalToggle(false))} />}
      </aside>
    );
  }

  const institutionCollapsed = mobile ? false : isCollapsed;
  const setupActive = setupLinks.some(({ to }) => (
    to === "/iot-sensors"
      ? location.pathname.startsWith("/iot-sensors")
      : location.pathname === to
  ));
  const creationLabel = canCreateIncident && canCreateDrill
    ? "New Incident / Drill"
    : canCreateIncident ? "New Incident" : "New Drill";

  /* ------------------------------ Render -------------------------------- */
  return (
    <aside
      className={`dms-institution-sidebar flex h-full shrink-0 flex-col overflow-hidden border-r border-slate-200/80 bg-white text-slate-950 shadow-[10px_0_35px_rgba(15,23,42,0.08)] transition-[width] duration-200 ease-out ${institutionCollapsed ? "w-[72px]" : "w-[264px]"}`}
      data-testid="institution-sidebar"
      data-collapsed={String(institutionCollapsed)}
      data-mobile={String(mobile)}
      onClickCapture={(event) => {
        if (mobile && event.target.closest("a")) onRequestClose?.();
      }}
    >
      <header className="flex h-[100px] shrink-0 items-center border-b border-slate-200/80 px-2">
        {mobile ? (
          <div className="grid w-full min-w-0 grid-cols-[40px_minmax(0,1fr)_40px] items-center gap-2">
            <span className="h-10 w-10" aria-hidden="true" />
            <span className="flex min-w-0 items-center justify-center gap-3">
              <img src={Logo} alt="DisasterMS Logo" className="h-12 w-12 shrink-0 object-contain" />
              <span className="min-w-0 leading-none">
                <span className="block truncate text-[20px] font-extrabold tracking-[-0.025em] text-slate-950">DisasterMS</span>
                <span className="mt-2 block whitespace-nowrap text-[9px] font-bold tracking-[0.2em] text-blue-600">PLAN · ACT · LEAD</span>
              </span>
            </span>
            <button
              type="button"
              onClick={onRequestClose}
              data-sidebar-close
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[9px] border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
              aria-label="Close navigation menu"
              title="Close navigation menu"
            >
              <X className="h-[18px] w-[18px]" strokeWidth={1.8} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={toggleSidebar}
            className="group flex h-14 w-full min-w-0 items-center justify-start gap-3 rounded-xl p-0 text-left outline-none transition-colors hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-blue-500/40"
            aria-label={institutionCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={institutionCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <img
              src={Logo}
              alt="DisasterMS Logo"
              className="h-14 w-14 shrink-0 object-contain"
            />
            {!institutionCollapsed && (
              <span className="min-w-0 leading-none">
                <span className="block truncate text-[20px] font-extrabold tracking-[-0.025em] text-slate-950">DisasterMS</span>
                <span className="mt-2 block whitespace-nowrap text-[9px] font-bold tracking-[0.2em] text-blue-600">PLAN · ACT · LEAD</span>
              </span>
            )}
          </button>
        )}
      </header>

      <nav
        className="dms-institution-sidebar-scroll flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden px-3 py-4"
        aria-label="Institution navigation"
      >
        <div className="space-y-1.5">
          <NavItem
            icon={LayoutDashboard}
            text={dashboardName}
            to="/dashboard"
            active={["/", "/dashboard"].includes(location.pathname)}
            collapsed={institutionCollapsed}
          />

          {canManageAccess && !isAdmin && (
            <NavItem
              icon={User}
              text="Users & Access"
              to="/users"
              active={location.pathname.startsWith("/users")}
              collapsed={institutionCollapsed}
            />
          )}

          {reportsEnabled && (webAccessLevel === WEB_ACCESS.AUDITOR || activeSupportSession) && (
            <NavItem
              icon={FileBarChart}
              text="Reports"
              to="/reports"
              active={location.pathname.startsWith("/reports")}
              collapsed={institutionCollapsed}
            />
          )}

          {webAccessLevel === WEB_ACCESS.PLATFORM_SUPPORT && (
            <NavItem
              icon={KeyRound}
              text="Support Access"
              to="/support-access"
              active={location.pathname.startsWith("/support-access")}
              collapsed={institutionCollapsed}
            />
          )}

          <InstitutionNotificationsNavItem
            text="Notifications"
            active={location.pathname === "/notifications"}
            collapsed={institutionCollapsed}
          />

          {(isAdmin || webAccessLevel === WEB_ACCESS.OPERATIONAL) && (
            <NavItem
              icon={Library}
              text="Scenario Library"
              to="/scenario-library"
              active={location.pathname.startsWith("/scenario-library")}
              collapsed={institutionCollapsed}
            />
          )}

          {isAdmin && (
            <NavItem
              icon={Globe}
              text="Map"
              to="/map"
              active={location.pathname === "/map"}
              collapsed={institutionCollapsed}
            />
          )}

          {isAdmin && reportsEnabled && (
            <NavItem
              icon={FileBarChart}
              text="Reports"
              to="/reports"
              active={location.pathname.startsWith("/reports")}
              collapsed={institutionCollapsed}
            />
          )}
        </div>

        {isAdmin && (
          <section className="mt-4 border-t border-slate-200/80 pt-4">
            <button
              type="button"
              onClick={() => setSetupOpen(!setupOpen)}
              title={institutionCollapsed ? "DisasterMS Setup" : undefined}
              aria-label={institutionCollapsed ? "DisasterMS Setup" : undefined}
              aria-expanded={setupOpen}
              className={`group flex h-11 items-center rounded-[9px] border transition-colors duration-150 ${
                setupOpen || setupActive
                  ? "border-blue-200 bg-blue-50 text-blue-700"
                  : `${institutionCollapsed ? "border-slate-200" : "border-transparent"} text-slate-600 hover:border-slate-200 hover:bg-slate-50 hover:text-slate-950`
              } ${institutionCollapsed ? "mx-auto w-11 justify-center p-0" : "w-full gap-3 px-3"}`}
            >
              <Settings className="h-[18px] w-[18px] shrink-0" strokeWidth={1.8} />
              {!institutionCollapsed && (
                <>
                  <span className="min-w-0 flex-1 truncate text-left text-[13px] font-medium">DisasterMS Setup</span>
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 transition-transform duration-200 ${setupOpen ? "rotate-180" : ""}`}
                    strokeWidth={1.8}
                  />
                </>
              )}
            </button>

            {setupOpen && (
              <div className={institutionCollapsed
                ? "mt-2 space-y-1.5"
                : "ml-4 mt-2 space-y-1 border-l border-slate-200 pl-3"}
              >
                {setupLinks.map(({ icon, text, to }) => (
                  <NavItem
                    key={to}
                    icon={icon}
                    text={text}
                    to={to}
                    active={
                      to === "/iot-sensors"
                        ? location.pathname.startsWith("/iot-sensors")
                        : location.pathname === to
                    }
                    collapsed={institutionCollapsed}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        <RecentIncidents
          isCollapsed={institutionCollapsed}
          onNavigate={mobile ? onRequestClose : undefined}
        />
      </nav>

      <footer className={`shrink-0 border-t border-slate-200/80 ${institutionCollapsed ? "space-y-2 px-3 py-3" : "space-y-2.5 px-4 py-4"}`}>
        {canCreateIncidentOrDrill && (
          <button
            type="button"
            onClick={handleNewIncident}
            data-testid="new-incident-btn"
            title={institutionCollapsed ? creationLabel : undefined}
            aria-label={creationLabel}
            className={`flex h-11 w-full items-center rounded-[9px] bg-red-600 font-semibold text-white shadow-sm transition-colors hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40 ${institutionCollapsed ? "justify-center p-0" : "gap-3 px-3"}`}
          >
            <Bell className="h-[18px] w-[18px] shrink-0" strokeWidth={1.8} />
            {!institutionCollapsed && <span className="truncate text-[13px]">{creationLabel}</span>}
          </button>
        )}

        <button
          type="button"
          onClick={handleLogout}
          title={institutionCollapsed ? "Logout" : undefined}
          aria-label="Logout"
          className={`flex h-11 w-full items-center rounded-[9px] border border-transparent text-slate-500 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500/30 ${institutionCollapsed ? "justify-center p-0" : "gap-3 px-3"}`}
        >
          <LogOut className="h-[18px] w-[18px] shrink-0" strokeWidth={1.8} />
          {!institutionCollapsed && <span className="text-[13px] font-medium">Logout</span>}
        </button>
      </footer>
      {isLogoutModalOpen && <LogoutModal onClose={() => dispatch(logoutModalToggle(false))} />}
    </aside>
  );
};

export default Sidebar;
